import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminEspecialidadesQuery } from '../../queries/appQueries';
import type { Especialidad as EspecialidadCompartida } from '../../services/especialidadService';
import {
  Button,
  Text,
  Group,
  Stack,
  Badge,
  TextInput,
  Select,
  Pagination,
  Modal,
  Textarea,
  ActionIcon,
  Alert,
  Card,
  SimpleGrid,
  Switch,
  Image,
  AspectRatio,
  Box,
  Menu,
  Avatar,
} from '@mantine/core';
import {
  IconVideo,
  IconPlus,
  IconEdit,
  IconTrash,
  IconFilter,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconPlayerPlay,
  IconDots,
  IconEye,
  IconEyeOff,
  IconChevronDown,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useMantineColorScheme } from '@mantine/core';
import CountUpNumber from '../../components/animations/CountUpNumber';
import { videoEstudioService } from '../../services/videoEstudioService';
import { VideoEstudio, VideoEstudioCreate } from '../../Models/VideoEstudio';
import { Especialidad } from '../../Models/Especialidad';
import { temaService, Tema } from '../../services/temaService';
import AdminPageHeader from '../../components/AdminPageHeader';
import PageTransition from '../../components/animations/PageTransition';
import PageSkeleton from '../../components/ui/PageSkeleton';
import SearchInput from '../../components/ui/SearchInput';
import enarmLogo from '../../assets/enarm_logo_noletter.png';
import { colorEspecialidad, capitalizarEspecialidad as capitalizar } from '../../utils/especialidad';

const AdminVideosPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const primaryBtn = { root: { backgroundColor: textColor, color: dark ? '#1e293b' : '#f7f3ee' } };

  const [videos, setVideos] = useState<VideoEstudio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [filterEspecialidad, setFilterEspecialidad] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);

  const [opened, { open, close }] = useDisclosure(false);
  const [previewOpened, { open: openPreview, close: closePreview }] = useDisclosure(false);
  const [editingVideo, setEditingVideo] = useState<VideoEstudio | null>(null);
  const [previewVideo, setPreviewVideo] = useState<VideoEstudio | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const especialidadesResult = useQuery(adminEspecialidadesQuery());
  const especialidades = (especialidadesResult.data as EspecialidadCompartida[] | undefined) ?? [];
  const [temas, setTemas] = useState<Tema[]>([]);
  const [selectedEspecialidadId, setSelectedEspecialidadId] = useState<number | null>(null);

  const [formData, setFormData] = useState<VideoEstudioCreate>({
    titulo: '',
    descripcion: '',
    youtubeUrl: '',
    especialidadId: 0,
    temaId: undefined,
    orden: undefined,
    activo: true
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedEspecialidadId) {
      const cargarTemas = async () => {
        try {
          const temasData = await temaService.getTemasByEspecialidad(selectedEspecialidadId);
          setTemas(temasData);
        } catch {
          setTemas([]);
        }
      };
      cargarTemas();
    } else {
      setTemas([]);
    }
  }, [selectedEspecialidadId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const videosData = await videoEstudioService.getAllAdmin();
      setVideos(videosData);
      setError(null);
    } catch {
      setError('Error al cargar los datos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (video.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEspecialidad = !filterEspecialidad || video.especialidadId.toString() === filterEspecialidad;
    return matchesSearch && matchesEspecialidad;
  });

  const totalPages = Math.ceil(filteredVideos.length / itemsPerPage);
  const paginatedVideos = filteredVideos.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Volver a la primera página al cambiar filtros/búsqueda/tamaño.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterEspecialidad, itemsPerPage]);

  const hayFiltrosActivos = !!(filterEspecialidad || searchTerm.trim());

  // Filtro desplegable estilo Reactivos.
  const filterMenu = (opts: {
    label: string;
    selectedId: string | null;
    items: Array<{ id: number; nombre: string }>;
    onSelect: (id: string | null) => void;
  }) => {
    const selected = opts.items.find((i) => i.id.toString() === opts.selectedId);
    return (
      <Menu shadow="md" width={250} position="bottom-start" withinPortal>
        <Menu.Target>
          <Button
            variant={opts.selectedId ? 'light' : 'default'}
            color="gray"
            radius="md"
            leftSection={<IconFilter size={16} />}
            rightSection={<IconChevronDown size={14} />}
          >
            {selected ? selected.nombre : opts.label}
          </Button>
        </Menu.Target>
        <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
          <Menu.Item
            leftSection={!opts.selectedId ? <IconCheck size={14} /> : <Box style={{ width: 14 }} />}
            onClick={() => opts.onSelect(null)}
          >
            {opts.label} (todas)
          </Menu.Item>
          <Menu.Divider />
          {opts.items.map((it) => {
            const sel = opts.selectedId === it.id.toString();
            return (
              <Menu.Item key={it.id} onClick={() => opts.onSelect(it.id.toString())} rightSection={sel ? <IconCheck size={14} /> : null}>
                <Text size="sm" fw={sel ? 700 : 500}>{it.nombre}</Text>
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    );
  };

  const handleOpenForm = (video?: VideoEstudio) => {
    if (video) {
      setEditingVideo(video);
      setFormData({
        titulo: video.titulo,
        descripcion: video.descripcion || '',
        youtubeUrl: video.youtubeUrl,
        especialidadId: video.especialidadId,
        temaId: video.temaId || undefined,
        orden: video.orden || undefined,
        activo: video.activo !== false
      });
      setSelectedEspecialidadId(video.especialidadId);
    } else {
      setEditingVideo(null);
      setFormData({
        titulo: '',
        descripcion: '',
        youtubeUrl: '',
        especialidadId: 0,
        temaId: undefined,
        orden: undefined,
        activo: true
      });
      setSelectedEspecialidadId(null);
    }
    open();
  };

  const handlePreview = (video: VideoEstudio) => {
    setPreviewVideo(video);
    openPreview();
  };

  const handleSubmit = async () => {
    if (!formData.titulo || !formData.youtubeUrl || !formData.especialidadId) {
      notifications.show({
        title: 'Error',
        message: 'Por favor completa los campos obligatorios (título, URL de YouTube y especialidad)',
        color: 'red'
      });
      return;
    }

    try {
      setFormLoading(true);
      if (editingVideo) {
        await videoEstudioService.update(editingVideo.id, formData);
        notifications.show({
          title: 'Video actualizado',
          message: 'El video se actualizó correctamente',
          color: 'green'
        });
      } else {
        await videoEstudioService.create(formData);
        notifications.show({
          title: 'Video creado',
          message: 'El video se creó correctamente',
          color: 'green'
        });
      }
      close();
      loadInitialData();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Error al guardar el video',
        color: 'red'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este video?')) return;

    try {
      await videoEstudioService.delete(id);
      notifications.show({
        title: 'Video eliminado',
        message: 'El video se eliminó correctamente',
        color: 'green'
      });
      loadInitialData();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Error al eliminar el video',
        color: 'red'
      });
    }
  };

  const handleToggleActivo = async (id: number) => {
    try {
      await videoEstudioService.toggleActivo(id);
      loadInitialData();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Error al cambiar el estado del video',
        color: 'red'
      });
    }
  };

  if (loading) {
    return <PageSkeleton variant="grid" count={3} />;
  }

  if (error) {
    return (
      <Box style={{ flex: 1, padding: '16px 24px' }}>
        <Alert icon={<IconInfoCircle size="1rem" />} title="Error" color="red">
          {error}
        </Alert>
      </Box>
    );
  }

  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };

  const statsCards = [
    { icon: IconVideo, value: videos.length, label: 'Total videos', duration: 2500 },
    { icon: IconCheck, value: videos.filter(v => v.activo).length, label: 'Activos', duration: 2000 },
    { icon: IconX, value: videos.filter(v => !v.activo).length, label: 'Inactivos', duration: 2000 },
  ];

  return (
    <PageTransition type="medical" duration={800}>
    <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
      <style>{`
        .video-card { border-radius: 14px; padding: 6px; transition: background-color 0.15s ease; }
        .video-card:hover { background-color: ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}; }
        .video-thumb { position: relative; width: 100%; height: 100%; border-radius: 10px; overflow: hidden; background-color: #000; }
        .video-thumb img { width: 100% !important; height: 100% !important; object-fit: cover !important; display: block; }
        .video-play { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.3); opacity: 0; transition: opacity 0.15s ease; }
        .video-card:hover .video-play { opacity: 1; }
      `}</style>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <AdminPageHeader
            title="Videos de Estudio"
            subtitle="Gestiona videos de YouTube para los estudiantes"
          />
          <Button leftSection={<IconPlus size={16} />} styles={primaryBtn} onClick={() => handleOpenForm()}>
            Nuevo Video
          </Button>
        </Group>

        {/* Estadísticas (paleta sobria) */}
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
          {statsCards.map((s) => (
            <Card key={s.label} withBorder radius="lg" p="lg" style={cardStyle}>
              <Stack gap="sm" align="stretch">
                <s.icon size={20} color={textColor} style={{ flexShrink: 0 }} />
                <Box style={{ minWidth: 0 }}>
                  <CountUpNumber
                    value={s.value}
                    duration={s.duration}
                    style={{ color: textColor, fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Space Grotesk, Inter, sans-serif', textAlign: 'left' }}
                  />
                  <Text size="xs" ta="left" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                    {s.label}
                  </Text>
                </Box>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {/* Búsqueda + filtro desplegable (sin card, estilo Reactivos) */}
        <Group gap="sm" align="center" wrap="wrap">
          <SearchInput
            placeholder="Buscar por título o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          {filterMenu({
            label: 'Especialidad',
            selectedId: filterEspecialidad,
            items: especialidades,
            onSelect: setFilterEspecialidad,
          })}
          {hayFiltrosActivos && (
            <Button variant="subtle" color="gray" onClick={() => { setSearchTerm(''); setFilterEspecialidad(null); }}>
              Limpiar
            </Button>
          )}
        </Group>

        {/* Grid de Video Cards */}
        {filteredVideos.length === 0 ? (
          <Card withBorder radius="lg" p="xl" style={cardStyle}>
            <Stack align="center" gap="md">
              <IconVideo size={48} style={{ opacity: 0.3, color: mutedColor }} />
              <Text size="sm" style={{ color: mutedColor }}>
                {hayFiltrosActivos ? 'Ningún video coincide con los filtros.' : 'No hay videos. Crea el primero.'}
              </Text>
            </Stack>
          </Card>
        ) : (
          <>
            <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="xs" verticalSpacing="md">
              {paginatedVideos.map((video) => {
                const espColor = colorEspecialidad(video.especialidadId);
                return (
                  <Box key={video.id} className="video-card" style={{ opacity: video.activo ? 1 : 0.55 }}>
                    {/* Thumbnail estilo YouTube */}
                    <AspectRatio ratio={16 / 9}>
                      <Box className="video-thumb">
                        <Image
                          src={video.thumbnailUrl || `https://img.youtube.com/vi/${video.youtubeVideoId}/hqdefault.jpg`}
                          alt={video.titulo}
                          fallbackSrc="https://placehold.co/640x360/1e293b/94a3b8?text=Sin+Thumbnail"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {/* Play overlay (hover en toda la card) */}
                        <Box className="video-play" onClick={() => handlePreview(video)} style={{ cursor: 'pointer' }}>
                          <IconPlayerPlay size={44} color="white" />
                        </Box>
                        {/* Badge de estado */}
                        <Badge
                          variant="filled"
                          color={video.activo ? 'green' : 'red'}
                          size="sm"
                          style={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}
                        >
                          {video.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                        {/* Menú de acciones */}
                        <Box style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }} onClick={(e) => e.stopPropagation()}>
                          <Menu shadow="md" width={180} position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="filled"
                                color="dark"
                                radius="xl"
                                size="md"
                                style={{ background: 'rgba(0,0,0,0.6)', border: 'none' }}
                              >
                                <IconDots size={16} color="white" />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item leftSection={<IconPlayerPlay size={14} />} onClick={() => handlePreview(video)}>
                                Vista previa
                              </Menu.Item>
                              <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => handleOpenForm(video)}>
                                Editar
                              </Menu.Item>
                              <Menu.Item
                                leftSection={video.activo ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                                onClick={() => handleToggleActivo(video.id)}
                              >
                                {video.activo ? 'Desactivar' : 'Activar'}
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleDelete(video.id)}>
                                Eliminar
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Box>
                      </Box>
                    </AspectRatio>

                    {/* Info estilo YouTube: avatar (logo) + título y debajo especialidad · tema */}
                    <Group gap="xs" align="flex-start" wrap="nowrap" mt="xs">
                      <Avatar
                        src={enarmLogo}
                        size={32}
                        radius="xl"
                        style={{ flexShrink: 0, backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.06)' }}
                      />
                      <Box style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <Text size="sm" fw={600} ta="left" lineClamp={2} style={{ color: textColor, lineHeight: 1.3 }}>
                          {video.titulo}
                        </Text>
                        <Group gap={5} mt={2} wrap="wrap" justify="flex-start">
                          <Text size="11px" fw={600} ta="left" style={{ color: espColor }}>
                            {capitalizar(video.especialidadNombre)}
                          </Text>
                          {video.temaNombre && (
                            <Text size="11px" ta="left" c="dimmed">· {capitalizar(video.temaNombre)}</Text>
                          )}
                        </Group>
                      </Box>
                    </Group>
                  </Box>
                );
              })}
            </SimpleGrid>

            {/* Footer: "Mostrando X de Y" + tamaño (izq), paginación (der) */}
            <Group justify="space-between" align="center" mt="md" wrap="wrap" gap="sm">
              <Group gap="xs" align="center">
                <Text size="xs" style={{ color: mutedColor }}>
                  Mostrando {paginatedVideos.length} de {filteredVideos.length.toLocaleString('es-MX')}
                </Text>
                <Select
                  size="xs"
                  w={92}
                  data={['9', '18', '36', '60']}
                  value={String(itemsPerPage)}
                  onChange={(v) => v && setItemsPerPage(Number(v))}
                  allowDeselect={false}
                  aria-label="Videos por página"
                />
              </Group>
              {totalPages > 1 && (
                <Pagination value={page} onChange={setPage} total={totalPages} color="gray" size="sm" />
              )}
            </Group>
          </>
        )}
      </Stack>

      {/* Modal vista previa */}
      <Modal
        opened={previewOpened}
        onClose={closePreview}
        title={previewVideo?.titulo || 'Vista previa'}
        size="xl"
        centered
      >
        {previewVideo && (
          <Stack gap="md">
            <AspectRatio ratio={16 / 9}>
              <iframe
                src={previewVideo.embedUrl || `https://www.youtube.com/embed/${previewVideo.youtubeVideoId}`}
                title={previewVideo.titulo}
                style={{ border: 0, borderRadius: '12px' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </AspectRatio>
            {previewVideo.descripcion && (
              <Text size="sm" c="dimmed">{previewVideo.descripcion}</Text>
            )}
            <Group gap="xs">
              <Badge variant="light" color="blue">{previewVideo.especialidadNombre}</Badge>
              {previewVideo.temaNombre && (
                <Badge variant="light" color="teal">{previewVideo.temaNombre}</Badge>
              )}
              <Badge variant="light" color={previewVideo.activo ? 'green' : 'red'}>
                {previewVideo.activo ? 'Activo' : 'Inactivo'}
              </Badge>
            </Group>
            <Group justify="flex-end" gap="xs">
              <Button variant="outline" size="sm" leftSection={<IconEdit size={14} />} onClick={() => { closePreview(); handleOpenForm(previewVideo); }}>
                Editar
              </Button>
              <Button variant="outline" color="red" size="sm" leftSection={<IconTrash size={14} />} onClick={() => { closePreview(); handleDelete(previewVideo.id); }}>
                Eliminar
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Modal de formulario */}
      <Modal
        opened={opened}
        onClose={close}
        title={editingVideo ? 'Editar Video' : 'Nuevo Video'}
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Título"
            placeholder="Título del video"
            required
            value={formData.titulo}
            onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
          />

          <Textarea
            label="Descripción"
            placeholder="Descripción del video (opcional)"
            minRows={2}
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
          />

          <TextInput
            label="URL de YouTube"
            placeholder="https://www.youtube.com/watch?v=..."
            required
            value={formData.youtubeUrl}
            onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
          />

          {/* Preview del thumbnail en el formulario */}
          {formData.youtubeUrl && formData.youtubeUrl.includes('youtube.com/watch?v=') && (
            <AspectRatio ratio={16 / 9} maw={300}>
              <Image
                src={`https://img.youtube.com/vi/${formData.youtubeUrl.split('watch?v=')[1]?.split('&')[0]}/mqdefault.jpg`}
                alt="Preview"
                radius="md"
                fallbackSrc="https://placehold.co/320x180/1e293b/94a3b8?text=Preview"
              />
            </AspectRatio>
          )}

          <Select
            label="Especialidad"
            placeholder="Selecciona una especialidad"
            required
            value={selectedEspecialidadId ? selectedEspecialidadId.toString() : null}
            onChange={(value) => {
              const id = value ? parseInt(value) : null;
              setSelectedEspecialidadId(id);
              setFormData({ ...formData, especialidadId: id || 0, temaId: undefined });
              setTemas([]);
            }}
            data={especialidades.map(esp => ({
              value: esp.id.toString(),
              label: esp.nombre
            }))}
          />

          <Select
            label="Tema (opcional)"
            placeholder="Selecciona un tema"
            disabled={!selectedEspecialidadId}
            value={formData.temaId ? formData.temaId.toString() : null}
            onChange={(value) => setFormData({ ...formData, temaId: value ? parseInt(value) : undefined })}
            data={temas.map(tema => ({
              value: tema.id.toString(),
              label: tema.nombre
            }))}
            key={selectedEspecialidadId?.toString() || 'no-especialidad'}
            searchable
            clearable
            nothingFoundMessage="No hay temas disponibles"
          />

          <TextInput
            label="Orden"
            placeholder="Orden de aparición (opcional)"
            type="number"
            value={formData.orden?.toString() || ''}
            onChange={(e) => setFormData({ ...formData, orden: e.target.value ? parseInt(e.target.value) : undefined })}
          />

          <Switch
            label="Video activo"
            checked={formData.activo !== false}
            onChange={(e) => setFormData({ ...formData, activo: e.currentTarget.checked })}
          />

          <Group justify="flex-end">
            <Button variant="outline" onClick={close}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} loading={formLoading}>
              {editingVideo ? 'Actualizar' : 'Crear'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
    </PageTransition>
  );
};

export default AdminVideosPage;
